/**
 * Module FACTURATION KAIZEN — Factures, Reçus & Soumissions (taxes Québec)
 *
 * 2 onglets :
 *  - Documents : créer une facture / reçu / soumission avec calcul auto TPS + TVQ + PDF
 *  - Profil fiscal : numéros de taxe (NEQ, TPS, TVQ) + coordonnées affichés sur les documents
 */
import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Receipt, Plus, Trash, FilePdf, FloppyDisk, Buildings, X, EnvelopeSimple, DeviceMobile, ArrowsClockwise, ChartBar, CheckCircle, Copy, Tag, SealCheck, Warning, Bell } from "@phosphor-icons/react";

const TPS_RATE = 0.05;
const TVQ_RATE = 0.09975;

const DOC_TYPES = [
  { value: "facture", label: "Facture" },
  { value: "recu", label: "Reçu" },
  { value: "soumission", label: "Soumission" },
];

const money = (x) => `${Number(x || 0).toFixed(2)} $`;

function openPdf(docId) {
  toast.message("Génération du PDF…");
  api
    .get(`/facturation/documents/${docId}/pdf`, { responseType: "blob" })
    .then((r) => {
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      window.open(url, "_blank");
    })
    .catch(() => toast.error("Erreur lors de la génération du PDF"));
}

export default function FacturationModule() {
  const [tab, setTab] = useState("documents");
  const [docs, setDocs] = useState([]);
  const [profil, setProfil] = useState(null);
  const [showModal, setShowModal] = useState(false);

  async function loadDocs() {
    try {
      const r = await api.get("/facturation/documents");
      setDocs(r.data.documents || []);
    } catch {
      toast.error("Erreur de chargement des documents");
    }
  }
  async function loadProfil() {
    try {
      const r = await api.get("/facturation/profil");
      setProfil(r.data.profil);
    } catch {
      toast.error("Erreur de chargement du profil");
    }
  }

  useEffect(() => {
    loadDocs();
    loadProfil();
  }, []);

  return (
    <div className="space-y-6" data-testid="facturation-module">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-zinc-100 flex items-center gap-2">
            <Receipt size={30} weight="fill" className="text-red-500" /> Facturation
          </h1>
          <p className="text-sm text-zinc-400 mt-1">
            Factures, reçus & soumissions — taxes du Québec (TPS 5 % + TVQ 9,975 %)
          </p>
        </div>
        {tab === "documents" && (
          <button
            onClick={() => setShowModal(true)}
            data-testid="new-document-btn"
            className="bg-red-600 hover:bg-red-500 text-white font-semibold px-4 py-2 rounded-lg flex items-center gap-2"
          >
            <Plus size={18} weight="bold" /> Nouveau document
          </button>
        )}
      </div>

      <div className="flex gap-2 border-b border-zinc-800">
        {[
          { id: "documents", label: "Documents" },
          { id: "revenus", label: "Revenus" },
          { id: "services", label: "Services" },
          { id: "profil", label: "Profil fiscal" },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`tab-${t.id}`}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === t.id
                ? "border-red-500 text-red-400"
                : "border-transparent text-zinc-400 hover:text-zinc-200"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "documents" && <DocumentsList docs={docs} onReload={loadDocs} />}
      {tab === "revenus" && <RevenusPanel />}
      {tab === "services" && <ServicesPanel />}
      {tab === "profil" && <ProfilForm profil={profil} onSaved={loadProfil} />}

      {showModal && (
        <DocumentModal
          profil={profil}
          onClose={() => setShowModal(false)}
          onDone={() => {
            setShowModal(false);
            loadDocs();
          }}
        />
      )}
    </div>
  );
}

function DocumentsList({ docs, onReload }) {
  async function del(id) {
    if (!window.confirm("Supprimer ce document ?")) return;
    try {
      await api.delete(`/facturation/documents/${id}`);
      toast.success("Document supprimé");
      onReload();
    } catch {
      toast.error("Erreur de suppression");
    }
  }

  async function sendEmail(d) {
    const to = window.prompt(
      "Envoyer ce document par courriel à :",
      d.client_email || ""
    );
    if (to === null) return;
    if (!to.trim()) return toast.error("Courriel requis");
    toast.message("Envoi en cours…");
    try {
      await api.post(`/facturation/documents/${d.id}/send`, { to: to.trim() });
      toast.success(`Document ${d.number} envoyé à ${to.trim()}`);
      onReload();
    } catch (e) {
      const detail = e?.response?.data?.detail || "";
      if (detail.includes("Gmail") || e?.response?.status === 400) {
        toast.error("Connecte d'abord ton compte Gmail (menu QuickBooks/Intégrations) — " + detail);
      } else {
        toast.error("Échec de l'envoi : " + detail);
      }
    }
  }

  async function sendSms(d) {
    const to = window.prompt("Envoyer par SMS au numéro du client :", "");
    if (to === null) return;
    if (!to.trim()) return toast.error("Numéro requis");
    toast.message("Envoi du SMS…");
    try {
      await api.post(`/facturation/documents/${d.id}/send-sms`, { to: to.trim() });
      toast.success(`SMS envoyé à ${to.trim()}`);
      onReload();
    } catch (e) {
      toast.error("Échec SMS : " + (e?.response?.data?.detail || e.message));
    }
  }

  async function syncQbo(d) {
    if (d.doc_type === "soumission") return toast.error("Une soumission ne se synchronise pas.");
    toast.message("Synchronisation vers QuickBooks…");
    try {
      const r = await api.post(`/facturation/documents/${d.id}/sync-qbo`);
      if (r.data.already_synced) toast.info(`Déjà dans QuickBooks (#${r.data.qbo_id})`);
      else toast.success(`Synchronisé vers QuickBooks (${r.data.qbo_type} #${r.data.qbo_id})`);
      onReload();
    } catch (e) {
      toast.error("Échec QuickBooks : " + (e?.response?.data?.detail || e.message));
    }
  }

  async function togglePaid(d) {
    const next = d.status === "paye" ? "impaye" : "paye";
    try {
      await api.patch(`/facturation/documents/${d.id}/status`, { status: next });
      toast.success(next === "paye" ? `Facture ${d.number} marquée PAYÉE` : `Facture ${d.number} marquée IMPAYÉE`);
      onReload();
    } catch (e) {
      toast.error("Erreur de statut : " + (e?.response?.data?.detail || e.message));
    }
  }

  async function duplicate(d) {
    try {
      const r = await api.post(`/facturation/documents/${d.id}/duplicate`);
      toast.success(`Document dupliqué → ${r.data.document.number}`);
      onReload();
    } catch (e) {
      toast.error("Erreur de duplication : " + (e?.response?.data?.detail || e.message));
    }
  }

  async function relance(d) {
    const to = window.prompt("Relancer cette facture impayée par courriel à :", d.client_email || "");
    if (to === null) return;
    if (!to.trim()) return toast.error("Courriel requis");
    toast.message("Envoi de la relance…");
    try {
      const r = await api.post(`/facturation/documents/${d.id}/relance`, { channel: "email", to: to.trim() });
      toast.success(`Relance #${r.data.reminder_count} envoyée à ${to.trim()}`);
      onReload();
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message;
      if (detail.includes("Gmail")) {
        toast.error("Connecte d'abord ton compte Gmail (menu Intégrations) — " + detail);
      } else {
        toast.error("Échec de la relance : " + detail);
      }
    }
  }

  if (!docs.length) {
    return (
      <div className="text-center py-16 text-zinc-500" data-testid="empty-docs">
        Aucun document. Crée ta première facture, ton premier reçu ou ta première soumission.
      </div>
    );
  }

  const typeLabel = { facture: "Facture", recu: "Reçu", soumission: "Soumission" };
  const typeColor = {
    facture: "bg-blue-500/15 text-blue-300",
    recu: "bg-green-500/15 text-green-300",
    soumission: "bg-amber-500/15 text-amber-300",
  };

  return (
    <div className="overflow-x-auto" data-testid="documents-list">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-zinc-500 border-b border-zinc-800">
            <th className="py-2 px-3">N°</th>
            <th className="py-2 px-3">Type</th>
            <th className="py-2 px-3">Client</th>
            <th className="py-2 px-3">Date</th>
            <th className="py-2 px-3 text-right">Total</th>
            <th className="py-2 px-3 text-center">Statut</th>
            <th className="py-2 px-3 text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {docs.map((d) => (
            <tr key={d.id} className="border-b border-zinc-900 hover:bg-zinc-900/50" data-testid={`doc-row-${d.number}`}>
              <td className="py-2 px-3 font-mono text-zinc-300">{d.number}</td>
              <td className="py-2 px-3">
                <span className={`px-2 py-0.5 rounded text-xs ${typeColor[d.doc_type]}`}>
                  {typeLabel[d.doc_type]}
                </span>
              </td>
              <td className="py-2 px-3 text-zinc-200">{d.client_name}</td>
              <td className="py-2 px-3 text-zinc-400">{d.date}</td>
              <td className="py-2 px-3 text-right font-semibold text-zinc-100">{money(d.total)}</td>
              <td className="py-2 px-3 text-center">
                {d.doc_type === "soumission" ? (
                  <span className="text-zinc-600 text-xs">—</span>
                ) : (d.doc_type === "recu" || d.status === "paye") ? (
                  <span data-testid={`status-${d.number}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-green-500/15 text-green-300">
                    <SealCheck size={13} weight="fill" /> Payé
                  </span>
                ) : (
                  <span data-testid={`status-${d.number}`} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs ${(d.due_date && d.due_date < new Date().toISOString().slice(0, 10)) ? "bg-red-600/25 text-red-200" : "bg-red-500/15 text-red-300"}`}>
                    <Warning size={13} weight="fill" /> {(d.due_date && d.due_date < new Date().toISOString().slice(0, 10)) ? "En retard" : "Impayé"}{d.due_date ? ` · ${d.due_date}` : ""}
                  </span>
                )}
              </td>
              <td className="py-2 px-3">
                <div className="flex items-center justify-end gap-2">
                  {d.doc_type === "facture" && d.status !== "paye" && (
                    <button
                      onClick={() => relance(d)}
                      data-testid={`relance-btn-${d.number}`}
                      className="text-orange-400 hover:text-orange-300"
                      title={d.reminder_count ? `Relancer (déjà relancé ${d.reminder_count}×)` : "Relancer par courriel"}
                    >
                      <Bell size={18} weight={d.reminder_count ? "fill" : "regular"} />
                    </button>
                  )}
                  {d.doc_type === "facture" && (
                    <button
                      onClick={() => togglePaid(d)}
                      data-testid={`toggle-paid-btn-${d.number}`}
                      className={d.status === "paye" ? "text-amber-400 hover:text-amber-300" : "text-green-400 hover:text-green-300"}
                      title={d.status === "paye" ? "Marquer impayée" : "Marquer payée"}
                    >
                      <SealCheck size={19} weight={d.status === "paye" ? "regular" : "fill"} />
                    </button>
                  )}
                  <button
                    onClick={() => duplicate(d)}
                    data-testid={`duplicate-btn-${d.number}`}
                    className="text-zinc-400 hover:text-zinc-200"
                    title="Dupliquer / réémettre"
                  >
                    <Copy size={18} weight="bold" />
                  </button>
                  <button
                    onClick={() => sendEmail(d)}
                    data-testid={`email-btn-${d.number}`}
                    className="text-blue-400 hover:text-blue-300"
                    title="Envoyer par courriel"
                  >
                    <EnvelopeSimple size={19} weight="fill" />
                  </button>
                  <button
                    onClick={() => sendSms(d)}
                    data-testid={`sms-btn-${d.number}`}
                    className="text-purple-400 hover:text-purple-300"
                    title="Envoyer par SMS"
                  >
                    <DeviceMobile size={19} weight="fill" />
                  </button>
                  {d.doc_type !== "soumission" && (
                    <button
                      onClick={() => syncQbo(d)}
                      data-testid={`qbo-btn-${d.number}`}
                      className={d.qbo_id ? "text-green-400" : "text-emerald-500 hover:text-emerald-400"}
                      title={d.qbo_id ? `Déjà dans QuickBooks (#${d.qbo_id})` : "Synchroniser vers QuickBooks"}
                    >
                      {d.qbo_id ? <CheckCircle size={19} weight="fill" /> : <ArrowsClockwise size={18} weight="bold" />}
                    </button>
                  )}
                  <button
                    onClick={() => openPdf(d.id)}
                    data-testid={`pdf-btn-${d.number}`}
                    className="text-red-400 hover:text-red-300"
                    title="Voir le PDF"
                  >
                    <FilePdf size={20} weight="fill" />
                  </button>
                  <button
                    onClick={() => del(d.id)}
                    data-testid={`del-btn-${d.number}`}
                    className="text-zinc-500 hover:text-red-400"
                    title="Supprimer"
                  >
                    <Trash size={18} />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RevenusPanel() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    api.get("/facturation/stats").then((r) => setStats(r.data)).catch(() => toast.error("Erreur de chargement des revenus"));
  }, []);

  if (!stats) return <div className="text-zinc-500 py-10">Chargement…</div>;

  const cards = [
    { label: "Encaissé (reçus)", value: stats.total_encaisse, color: "text-green-400", testid: "stat-encaisse" },
    { label: "À recevoir (factures)", value: stats.total_facture, color: "text-blue-400", testid: "stat-facture" },
    { label: `Impayé (${stats.count_impaye || 0})`, value: stats.total_impaye, color: "text-red-400", testid: "stat-impaye" },
    { label: "Soumissions (devis)", value: stats.total_soumission, color: "text-amber-400", testid: "stat-soumission" },
    { label: "TPS perçue", value: stats.tps_collectee, color: "text-zinc-300", testid: "stat-tps" },
    { label: "TVQ perçue", value: stats.tvq_collectee, color: "text-zinc-300", testid: "stat-tvq" },
    { label: "Synchronisés QBO", value: stats.synced_qbo, color: "text-emerald-400", testid: "stat-qbo", raw: true },
  ];
  const mois = Object.entries(stats.par_mois || {});

  return (
    <div className="space-y-6" data-testid="revenus-panel">
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
        {cards.map((c) => (
          <div key={c.label} className="bg-zinc-900/60 border border-zinc-800 rounded-xl p-4" data-testid={c.testid}>
            <div className="text-xs text-zinc-500">{c.label}</div>
            <div className={`text-2xl font-bold mt-1 ${c.color}`}>
              {c.raw ? c.value : money(c.value)}
            </div>
          </div>
        ))}
      </div>

      {mois.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-zinc-300 mb-2 flex items-center gap-2">
            <ChartBar size={18} weight="fill" className="text-red-500" /> Par mois
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-zinc-500 border-b border-zinc-800">
                  <th className="py-2 px-3">Mois</th>
                  <th className="py-2 px-3 text-right">Encaissé</th>
                  <th className="py-2 px-3 text-right">Facturé</th>
                </tr>
              </thead>
              <tbody>
                {mois.map(([m, v]) => (
                  <tr key={m} className="border-b border-zinc-900" data-testid={`mois-${m}`}>
                    <td className="py-2 px-3 text-zinc-300 font-mono">{m}</td>
                    <td className="py-2 px-3 text-right text-green-400">{money(v.recu)}</td>
                    <td className="py-2 px-3 text-right text-blue-400">{money(v.facture)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function ServicesPanel() {
  const [services, setServices] = useState([]);
  const [desc, setDesc] = useState("");
  const [price, setPrice] = useState("");
  const [loading, setLoading] = useState(true);

  async function load() {
    try {
      const r = await api.get("/facturation/services");
      setServices(r.data.services || []);
    } catch {
      toast.error("Erreur de chargement des services");
    }
    setLoading(false);
  }
  useEffect(() => { load(); }, []);

  async function add() {
    if (!desc.trim()) return toast.error("Description requise");
    try {
      await api.post("/facturation/services", { description: desc.trim(), unit_price: Number(price) || 0 });
      toast.success("Service ajouté");
      setDesc(""); setPrice("");
      load();
    } catch (e) {
      toast.error("Erreur : " + (e?.response?.data?.detail || e.message));
    }
  }

  async function del(id) {
    if (!window.confirm("Supprimer ce service ?")) return;
    try {
      await api.delete(`/facturation/services/${id}`);
      toast.success("Service supprimé");
      load();
    } catch {
      toast.error("Erreur de suppression");
    }
  }

  if (loading) return <div className="text-zinc-500 py-10">Chargement…</div>;

  return (
    <div className="max-w-2xl space-y-4" data-testid="services-panel">
      <div className="flex items-center gap-2 text-zinc-300">
        <Tag size={22} weight="fill" className="text-red-500" />
        <span className="font-semibold">Services pré-enregistrés</span>
      </div>
      <p className="text-xs text-zinc-500">
        Crée tes articles fréquents ici pour les ajouter en 1 clic lors de la création d'un document.
      </p>

      <div className="flex gap-2 items-end">
        <div className="flex-1">
          <label className="text-xs text-zinc-400 mb-1 block">Description</label>
          <input
            type="text"
            value={desc}
            onChange={(e) => setDesc(e.target.value)}
            data-testid="service-desc-input"
            placeholder="ex: Cours privé (1 h)"
            className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-100"
          />
        </div>
        <div className="w-28">
          <label className="text-xs text-zinc-400 mb-1 block">Prix ($)</label>
          <input
            type="number"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            data-testid="service-price-input"
            placeholder="75"
            className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-100 text-right"
          />
        </div>
        <button
          onClick={add}
          data-testid="add-service-btn"
          className="bg-red-600 hover:bg-red-500 text-white font-semibold px-4 py-2 rounded-lg flex items-center gap-1"
        >
          <Plus size={16} weight="bold" /> Ajouter
        </button>
      </div>

      <div className="border border-zinc-800 rounded-xl divide-y divide-zinc-800" data-testid="services-list">
        {services.length === 0 && (
          <div className="text-center py-8 text-zinc-500 text-sm">Aucun service enregistré.</div>
        )}
        {services.map((s) => (
          <div key={s.id} className="flex items-center justify-between px-4 py-2.5" data-testid={`service-row-${s.id}`}>
            <span className="text-zinc-200 text-sm">{s.description}</span>
            <div className="flex items-center gap-4">
              <span className="text-zinc-400 font-mono text-sm">{money(s.unit_price)}</span>
              <button onClick={() => del(s.id)} className="text-zinc-500 hover:text-red-400" data-testid={`del-service-${s.id}`}>
                <Trash size={16} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ProfilForm({ profil, onSaved }) {
  const [form, setForm] = useState(profil || {});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (profil) setForm(profil);
  }, [profil]);

  const field = (key, label, placeholder = "") => (
    <div>
      <label className="text-xs text-zinc-400 mb-1 block">{label}</label>
      <input
        type="text"
        value={form[key] || ""}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
        data-testid={`profil-${key}`}
        placeholder={placeholder}
        className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-100"
      />
    </div>
  );

  async function save() {
    setSaving(true);
    try {
      await api.put("/facturation/profil", form);
      toast.success("Profil fiscal enregistré");
      onSaved();
    } catch {
      toast.error("Erreur d'enregistrement");
    }
    setSaving(false);
  }

  if (!profil) return <div className="text-zinc-500 py-10">Chargement…</div>;

  return (
    <div className="max-w-2xl space-y-4" data-testid="profil-form">
      <div className="flex items-center gap-2 text-zinc-300">
        <Buildings size={22} weight="fill" className="text-red-500" />
        <span className="font-semibold">Informations de l'entreprise</span>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        {field("legal_name", "Nom légal")}
        {field("brand_name", "Nom commercial / marque")}
        {field("neq", "NEQ")}
        {field("phone", "Téléphone")}
        {field("tps_number", "N° TPS (GST)")}
        {field("tvq_number", "N° TVQ (QST)")}
        {field("address", "Adresse")}
        {field("city", "Ville")}
        {field("province", "Province")}
        {field("postal_code", "Code postal")}
        {field("email", "Courriel")}
      </div>
      <button
        onClick={save}
        disabled={saving}
        data-testid="save-profil-btn"
        className="bg-red-600 hover:bg-red-500 disabled:opacity-50 text-white font-semibold px-5 py-2 rounded-lg flex items-center gap-2"
      >
        <FloppyDisk size={18} weight="fill" /> {saving ? "Enregistrement…" : "Enregistrer"}
      </button>
    </div>
  );
}

function DocumentModal({ profil, onClose, onDone }) {
  const [form, setForm] = useState({
    doc_type: "facture",
    client_name: "",
    client_email: "",
    client_address: "",
    date: new Date().toISOString().slice(0, 10),
    due_date: "",
    notes: "",
    apply_taxes: true,
  });
  const [items, setItems] = useState([{ description: "", quantity: 1, unit_price: 0 }]);
  const [services, setServices] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/facturation/services").then((r) => setServices(r.data.services || [])).catch(() => {});
  }, []);

  function addServiceLine(svcId) {
    const s = services.find((x) => x.id === svcId);
    if (!s) return;
    const line = { description: s.description, quantity: 1, unit_price: s.unit_price };
    setItems((prev) => {
      if (prev.length === 1 && !prev[0].description.trim() && !Number(prev[0].unit_price)) return [line];
      return [...prev, line];
    });
  }

  const totals = useMemo(() => {
    const subtotal = items.reduce((s, i) => s + Number(i.quantity || 0) * Number(i.unit_price || 0), 0);
    const tps = form.apply_taxes ? subtotal * TPS_RATE : 0;
    const tvq = form.apply_taxes ? subtotal * TVQ_RATE : 0;
    return { subtotal, tps, tvq, total: subtotal + tps + tvq };
  }, [items, form.apply_taxes]);

  function setItem(idx, key, val) {
    setItems(items.map((it, i) => (i === idx ? { ...it, [key]: val } : it)));
  }
  function addItem() {
    setItems([...items, { description: "", quantity: 1, unit_price: 0 }]);
  }
  function removeItem(idx) {
    setItems(items.filter((_, i) => i !== idx));
  }

  async function submit() {
    if (!form.client_name.trim()) return toast.error("Nom du client requis");
    const cleanItems = items
      .filter((i) => i.description.trim())
      .map((i) => ({ description: i.description, quantity: Number(i.quantity) || 0, unit_price: Number(i.unit_price) || 0 }));
    if (!cleanItems.length) return toast.error("Ajoute au moins une ligne d'article");

    setSaving(true);
    try {
      const r = await api.post("/facturation/documents", { ...form, items: cleanItems });
      toast.success(`Document ${r.data.document.number} créé`);
      onDone();
    } catch (e) {
      toast.error("Erreur: " + (e?.response?.data?.detail || e.message));
    }
    setSaving(false);
  }

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div
        className="bg-zinc-950 border border-red-500/30 rounded-xl max-w-2xl w-full p-5 space-y-4 my-8"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-bold text-red-400 flex items-center gap-2">
            <Receipt size={22} weight="fill" /> Nouveau document
          </h2>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-200" data-testid="modal-close">
            <X size={22} />
          </button>
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-zinc-400 mb-1 block">Type</label>
            <select
              value={form.doc_type}
              onChange={(e) => setForm({ ...form, doc_type: e.target.value })}
              data-testid="doc-type-select"
              className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-100"
            >
              {DOC_TYPES.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs text-zinc-400 mb-1 block">Date</label>
            <input
              type="date"
              value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })}
              data-testid="doc-date"
              className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-100"
            />
          </div>
          {form.doc_type === "facture" && (
            <div>
              <label className="text-xs text-zinc-400 mb-1 block">Échéance (optionnel)</label>
              <input
                type="date"
                value={form.due_date}
                onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                data-testid="doc-due-date"
                className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-100"
              />
            </div>
          )}
          <div>
            <label className="text-xs text-zinc-400 mb-1 block">Nom du client</label>
            <input
              type="text"
              value={form.client_name}
              onChange={(e) => setForm({ ...form, client_name: e.target.value })}
              data-testid="doc-client-name"
              placeholder="ex: Marie Dubois"
              className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-100"
            />
          </div>
          <div>
            <label className="text-xs text-zinc-400 mb-1 block">Courriel du client</label>
            <input
              type="email"
              value={form.client_email}
              onChange={(e) => setForm({ ...form, client_email: e.target.value })}
              data-testid="doc-client-email"
              className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-100"
            />
          </div>
        </div>

        {/* Lignes d'articles */}
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <span className="text-xs text-zinc-400 font-medium">Articles</span>
            <div className="flex items-center gap-2">
              {services.length > 0 && (
                <select
                  value=""
                  onChange={(e) => { addServiceLine(e.target.value); e.target.value = ""; }}
                  data-testid="service-quickpick"
                  className="bg-zinc-900 border border-zinc-700 rounded-lg px-2 py-1 text-xs text-zinc-200 max-w-[200px]"
                >
                  <option value="">+ Service pré-enregistré…</option>
                  {services.map((s) => (
                    <option key={s.id} value={s.id}>{s.description} — {money(s.unit_price)}</option>
                  ))}
                </select>
              )}
              <button onClick={addItem} data-testid="add-item-btn" className="text-red-400 hover:text-red-300 text-xs flex items-center gap-1">
                <Plus size={14} weight="bold" /> Ligne vide
              </button>
            </div>
          </div>
          {items.map((it, idx) => (
            <div key={idx} className="flex gap-2 items-center" data-testid={`item-row-${idx}`}>
              <input
                type="text"
                value={it.description}
                onChange={(e) => setItem(idx, "description", e.target.value)}
                placeholder="Description"
                data-testid={`item-desc-${idx}`}
                className="flex-1 bg-zinc-900 border border-zinc-700 rounded-lg px-2 py-1.5 text-sm text-zinc-100"
              />
              <input
                type="number"
                value={it.quantity}
                onChange={(e) => setItem(idx, "quantity", e.target.value)}
                placeholder="Qté"
                data-testid={`item-qty-${idx}`}
                className="w-16 bg-zinc-900 border border-zinc-700 rounded-lg px-2 py-1.5 text-sm text-zinc-100 text-right"
              />
              <input
                type="number"
                value={it.unit_price}
                onChange={(e) => setItem(idx, "unit_price", e.target.value)}
                placeholder="Prix"
                data-testid={`item-price-${idx}`}
                className="w-24 bg-zinc-900 border border-zinc-700 rounded-lg px-2 py-1.5 text-sm text-zinc-100 text-right"
              />
              <button onClick={() => removeItem(idx)} className="text-zinc-500 hover:text-red-400" data-testid={`item-remove-${idx}`}>
                <Trash size={16} />
              </button>
            </div>
          ))}
        </div>

        <label className="flex items-center gap-2 text-sm text-zinc-300">
          <input
            type="checkbox"
            checked={form.apply_taxes}
            onChange={(e) => setForm({ ...form, apply_taxes: e.target.checked })}
            data-testid="apply-taxes"
          />
          Appliquer les taxes (TPS 5 % + TVQ 9,975 %)
        </label>

        {/* Totaux */}
        <div className="bg-zinc-900/60 rounded-lg p-3 space-y-1 text-sm" data-testid="totals-box">
          <div className="flex justify-between text-zinc-400">
            <span>Sous-total</span><span data-testid="total-subtotal">{money(totals.subtotal)}</span>
          </div>
          {form.apply_taxes && (
            <>
              <div className="flex justify-between text-zinc-400">
                <span>TPS (5 %)</span><span data-testid="total-tps">{money(totals.tps)}</span>
              </div>
              <div className="flex justify-between text-zinc-400">
                <span>TVQ (9,975 %)</span><span data-testid="total-tvq">{money(totals.tvq)}</span>
              </div>
            </>
          )}
          <div className="flex justify-between font-bold text-red-400 text-base pt-1 border-t border-zinc-800">
            <span>TOTAL</span><span data-testid="total-grand">{money(totals.total)}</span>
          </div>
        </div>

        <textarea
          value={form.notes}
          onChange={(e) => setForm({ ...form, notes: e.target.value })}
          rows={2}
          placeholder="Note (optionnel)"
          data-testid="doc-notes"
          className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-zinc-100"
          maxLength={120}
        />

        <div className="flex gap-2">
          <button onClick={onClose} className="flex-1 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 py-2 rounded-lg">
            Annuler
          </button>
          <button
            onClick={submit}
            disabled={saving}
            data-testid="submit-doc-btn"
            className="flex-1 bg-red-600 hover:bg-red-500 disabled:opacity-50 text-white font-semibold py-2 rounded-lg"
          >
            {saving ? "Création…" : "Créer le document"}
          </button>
        </div>
      </div>
    </div>
  );
}
